// The ops calendar's day board (B1, B2) — who works when, and what they are
// doing, laid out left to right at the real time.
//
// Pure: everything here takes a `CalendarSource` (the shape both the demo and
// the real reads produce, see calendar.demo.ts) plus the clock, and returns
// plain data. The page and its components only draw what this returns, so the
// placement, the phases and every count can be tested without a browser.
//
// Times are MINUTES FROM THE START OF THE BOARD'S MANILA DAY, not clock times:
// a shift ending at midnight is 1440, one past it 1500. Placement and overlap
// stay simple arithmetic and never wrap.
//
// ⚠ DATA WALL: nothing in a source names a client. Rows are staff; a block
// carries a booking id and ref, never a client column (rules/data-wall.md).

import type {
  CalendarBookingKind,
  CalendarDispatchLegKind,
  OperationalCalendarBooking,
  OperationalCalendarDispatch,
  OperationalCalendarReadModels,
} from "@bednbody/types";
import { addCalendarDays, type CalendarStaffName, dayStartIso } from "./calendar-model";

/** The default visible day (Leon, 2026-10-22): 2 PM to midnight. */
export const DEFAULT_DAY_RANGE = {
  startMin: 14 * 60,
  endMin: 24 * 60,
} as const;

export interface CalendarHub {
  id: string;
  name: string;
}

/** What a calendar is drawn from — produced by the demo today, the real reads later. */
export interface CalendarSource {
  models: OperationalCalendarReadModels;
  names: readonly CalendarStaffName[];
  hubs: readonly CalendarHub[];
  /** Staff clocked in right now. Only meaningful on today's board. */
  clockedIn: ReadonlySet<string>;
  /** Service names each therapist is qualified for. */
  skills: ReadonlyMap<string, readonly string[]>;
  /**
   * What the live map knows right now that the plan cannot: an open SOS, a
   * lost signal, a break, a therapist waiting for her pickup. Real source:
   * the live map's ops status and open SOS alerts. Only read on today's board.
   */
  live?: ReadonlyMap<string, LiveState>;
}

export type LiveState = "sos" | "disconnected" | "on_break" | "waiting_pickup";

export type CalendarWho = "all" | "therapists" | "riders";

export interface CalendarFilters {
  hubId: string | null;
  who: CalendarWho;
  search: string;
}

export const NO_FILTERS: CalendarFilters = {
  hubId: null,
  who: "all",
  search: "",
};

export interface MinuteSpan {
  startMin: number;
  endMin: number;
}

export type BlockPhase = "done" | "now" | "next" | "unassigned";

interface BlockBase extends MinuteSpan {
  id: string;
  bookingId: string;
  ref: string;
  phase: BlockPhase;
}

export interface SessionBlock extends BlockBase {
  kind: "session";
  serviceName: string;
  durationMinutes: number;
  bookingKind: CalendarBookingKind;
  hubId: string;
  therapistId: string | null;
}

export interface LegBlock extends BlockBase {
  kind: CalendarDispatchLegKind;
  riderId: string;
  hubId: string;
}

export type BoardBlock = SessionBlock | LegBlock;

export type PersonStatus =
  | { kind: "day_off" }
  /** Not today's board, so "now" means nothing — just the plan. */
  | { kind: "scheduled" }
  | { kind: "not_clocked_in" }
  | { kind: "in_session"; untilMin: number }
  | { kind: "on_trip"; untilMin: number }
  | { kind: "free"; nextMin: number | null }
  | { kind: "sos" }
  | { kind: "disconnected" }
  | { kind: "on_break" }
  | { kind: "waiting_pickup" };

export interface BoardRow {
  staffId: string;
  name: string;
  initials: string;
  role: "therapist" | "rider";
  hubId: string;
  hubName: string;
  /** `null` on a day off. */
  shift: MinuteSpan | null;
  dayOff: boolean;
  clockedIn: boolean;
  /** Today only; `null` when the live map has nothing to add. */
  live: LiveState | null;
  skills: readonly string[];
  status: PersonStatus;
  blocks: BoardBlock[];
}

export interface CalendarDay {
  day: string;
  /** Minutes from this day's start to now — negative before it, past 1440 after it. */
  nowMin: number;
  /** Everyone with a published schedule entry for the day, every hub. */
  rows: BoardRow[];
  /** Every session that day, assigned or not, in start order. */
  sessions: SessionBlock[];
  /** Sessions with no therapist yet. */
  unassigned: SessionBlock[];
}

export interface BoardSection {
  hubId: string;
  hubName: string;
  therapistCount: number;
  riderCount: number;
  rows: BoardRow[];
}

export interface ScopedBoard {
  range: MinuteSpan;
  sections: BoardSection[];
  /** Hidden when only riders are shown. */
  unassigned: SessionBlock[];
}

/** Minutes from the start of `day` (Manila) to the instant `iso`. */
export function minutesFromDayStart(iso: string, day: string): number {
  return Math.round((Date.parse(iso) - Date.parse(dayStartIso(day))) / 60_000);
}

export function isToday(day: CalendarDay): boolean {
  return day.nowMin >= 0 && day.nowMin < 24 * 60;
}

export function initialsOf(name: string): string {
  const letters = name
    .split(/\s+/)
    .map((part) => part.replace(/[^\p{L}]/gu, "")[0])
    .filter(Boolean);
  return letters.slice(0, 2).join("").toUpperCase() || "?";
}

/** The real status says done or happening when it knows; the clock decides the rest. */
function timePhase(span: MinuteSpan, nowMin: number): "done" | "now" | "next" {
  if (span.endMin <= nowMin) return "done";
  if (span.startMin <= nowMin) return "now";
  return "next";
}

function sessionPhase(
  booking: OperationalCalendarBooking,
  span: MinuteSpan,
  nowMin: number,
): BlockPhase {
  if (booking.status === "completed") return "done";
  if (!booking.therapistId) return "unassigned";
  if (booking.status === "in_session") return "now";
  return timePhase(span, nowMin);
}

function legPhase(leg: OperationalCalendarDispatch, span: MinuteSpan, nowMin: number): BlockPhase {
  if (leg.legStatus === "completed") return "done";
  if (leg.legStatus === "active") return "now";
  return timePhase(span, nowMin);
}

function startsOnDay(iso: string, day: string): boolean {
  const at = Date.parse(iso);
  return (
    at >= Date.parse(dayStartIso(day)) && at < Date.parse(dayStartIso(addCalendarDays(day, 1)))
  );
}

/**
 * One status per person, most urgent first: an SOS and a lost signal outrank
 * the plan; a missed clock-in outranks the work; the work outranks a break or
 * a wait, which only describe someone who is otherwise free.
 */
function personStatus(row: Omit<BoardRow, "status">, nowMin: number, today: boolean): PersonStatus {
  if (row.dayOff || !row.shift) return { kind: "day_off" };
  if (!today) return { kind: "scheduled" };
  if (row.live === "sos") return { kind: "sos" };
  if (row.live === "disconnected") return { kind: "disconnected" };
  if (!row.clockedIn && row.shift.startMin <= nowMin) return { kind: "not_clocked_in" };
  const current = row.blocks.find((block) => block.startMin <= nowMin && block.endMin > nowMin);
  if (current) {
    return row.role === "rider"
      ? { kind: "on_trip", untilMin: current.endMin }
      : { kind: "in_session", untilMin: current.endMin };
  }
  if (row.live === "on_break") return { kind: "on_break" };
  if (row.live === "waiting_pickup") return { kind: "waiting_pickup" };
  const next = row.blocks.find((block) => block.startMin > nowMin);
  return { kind: "free", nextMin: next ? next.startMin : null };
}

/** One day of the board, every hub, before any filter. */
export function buildCalendarDay(source: CalendarSource, day: string, nowMs: number): CalendarDay {
  const nowMin = Math.floor((nowMs - Date.parse(dayStartIso(day))) / 60_000);
  const today = nowMin >= 0 && nowMin < 24 * 60;
  const nameById = new Map(source.names.map((entry) => [entry.id, entry]));
  const hubName = new Map(source.hubs.map((hub) => [hub.id, hub.name]));

  const bookings = source.models.bookings.filter(
    (booking) => booking.status !== "cancelled" && startsOnDay(booking.startsAt, day),
  );
  const refOf = new Map(source.models.bookings.map((booking) => [booking.bookingId, booking.ref]));

  const sessions: SessionBlock[] = bookings
    .map((booking) => {
      const span = {
        startMin: minutesFromDayStart(booking.startsAt, day),
        endMin: minutesFromDayStart(booking.endsAt, day),
      };
      return {
        kind: "session" as const,
        id: `session:${booking.bookingId}`,
        bookingId: booking.bookingId,
        ref: booking.ref,
        ...span,
        phase: sessionPhase(booking, span, nowMin),
        serviceName: booking.serviceName,
        durationMinutes: booking.durationMinutes,
        bookingKind: booking.bookingKind,
        hubId: booking.hubId,
        therapistId: booking.therapistId,
      };
    })
    .sort((a, b) => a.startMin - b.startMin);

  const legs: LegBlock[] = source.models.dispatches
    .filter((leg) => leg.legStatus !== "superseded" && startsOnDay(leg.plannedStart, day))
    .map((leg) => {
      const span = {
        startMin: minutesFromDayStart(leg.plannedStart, day),
        endMin: minutesFromDayStart(leg.plannedEnd, day),
      };
      return {
        kind: leg.kind,
        id: `leg:${leg.dispatchLegId}`,
        bookingId: leg.bookingId,
        ref: refOf.get(leg.bookingId) ?? "Booking",
        ...span,
        phase: legPhase(leg, span, nowMin),
        riderId: leg.riderId,
        hubId: leg.hubId,
      };
    })
    .sort((a, b) => a.startMin - b.startMin);

  const rows = source.models.staff
    .filter((entry) => entry.scheduleDate === day && entry.staffActive)
    .map((entry): BoardRow => {
      const name = nameById.get(entry.staffId)?.name ?? "Staff member";
      const dayOff = entry.kind === "rest_day" || !entry.dutyStart || !entry.dutyEnd;
      const blocks: BoardBlock[] =
        entry.staffRole === "rider"
          ? legs.filter((leg) => leg.riderId === entry.staffId)
          : sessions.filter((session) => session.therapistId === entry.staffId);
      const base = {
        staffId: entry.staffId,
        name,
        initials: initialsOf(name),
        role: entry.staffRole,
        hubId: entry.hubId,
        hubName: hubName.get(entry.hubId) ?? nameById.get(entry.staffId)?.hubName ?? "Hub",
        shift:
          dayOff || !entry.dutyStart || !entry.dutyEnd
            ? null
            : {
                startMin: minutesFromDayStart(entry.dutyStart, day),
                endMin: minutesFromDayStart(entry.dutyEnd, day),
              },
        dayOff,
        clockedIn: source.clockedIn.has(entry.staffId),
        live: today ? (source.live?.get(entry.staffId) ?? null) : null,
        skills: source.skills.get(entry.staffId) ?? [],
        blocks,
      };
      return { ...base, status: personStatus(base, nowMin, today) };
    });

  return {
    day,
    nowMin,
    rows,
    sessions,
    unassigned: sessions.filter((session) => session.phase === "unassigned"),
  };
}

/** The hours that fit the day: 2 PM–midnight unless a shift or block reaches past it. */
export function dayRange(spans: readonly MinuteSpan[]): MinuteSpan {
  let startMin: number = DEFAULT_DAY_RANGE.startMin;
  let endMin: number = DEFAULT_DAY_RANGE.endMin;
  for (const span of spans) {
    startMin = Math.min(startMin, Math.floor(span.startMin / 60) * 60);
    endMin = Math.max(endMin, Math.ceil(span.endMin / 60) * 60);
  }
  return { startMin: Math.max(startMin, 0), endMin: Math.min(endMin, 30 * 60) };
}

/** Left and width as a percentage of the visible range, clipped to it. */
export function placement(range: MinuteSpan, span: MinuteSpan): { left: number; width: number } {
  const total = range.endMin - range.startMin;
  const start = Math.max(span.startMin, range.startMin);
  const end = Math.min(span.endMin, range.endMin);
  return {
    left: ((start - range.startMin) / total) * 100,
    width: (Math.max(end - start, 0) / total) * 100,
  };
}

function normalise(text: string): string {
  return text.trim().toLowerCase();
}

export function inHub(filters: CalendarFilters, hubId: string): boolean {
  return filters.hubId === null || filters.hubId === hubId;
}

function whoMatches(filters: CalendarFilters, role: BoardRow["role"]): boolean {
  return (
    filters.who === "all" ||
    (filters.who === "therapists" && role === "therapist") ||
    (filters.who === "riders" && role === "rider")
  );
}

/** The board as drawn: hub sections in the source's hub order, filters applied once. */
export function scopeCalendarDay(
  day: CalendarDay,
  filters: CalendarFilters,
  hubs: readonly CalendarHub[],
): ScopedBoard {
  const query = normalise(filters.search);
  const matches = (...values: string[]) =>
    query === "" || values.some((value) => normalise(value).includes(query));

  const rows = day.rows.filter(
    (row) =>
      inHub(filters, row.hubId) &&
      whoMatches(filters, row.role) &&
      matches(row.name, ...row.blocks.map((block) => block.ref)),
  );
  const unassigned =
    filters.who === "riders"
      ? []
      : day.unassigned.filter(
          (session) => inHub(filters, session.hubId) && matches(session.ref, session.serviceName),
        );

  const byRoleThenName = (a: BoardRow, b: BoardRow) =>
    a.role === b.role ? a.name.localeCompare(b.name) : a.role === "therapist" ? -1 : 1;
  const sections = hubs.flatMap((hub): BoardSection[] => {
    const hubRows = rows.filter((row) => row.hubId === hub.id).sort(byRoleThenName);
    if (hubRows.length === 0) return [];
    const therapistCount = hubRows.filter((row) => row.role === "therapist").length;
    return [
      {
        hubId: hub.id,
        hubName: hub.name,
        therapistCount,
        riderCount: hubRows.length - therapistCount,
        rows: hubRows,
      },
    ];
  });

  const spans: MinuteSpan[] = [
    ...unassigned,
    ...rows.flatMap((row) => [...(row.shift ? [row.shift] : []), ...row.blocks]),
  ];
  return { range: dayRange(spans), sections, unassigned };
}

/** "5 PM", "5:30 PM", "12 AM" — minutes past 1440 roll into the next day. */
export function clockLabel(minutes: number): string {
  return `${shortClock(minutes)} ${Math.floor(minutes / 60) % 24 >= 12 ? "PM" : "AM"}`;
}

/** "5", "5:30" — for ranges where the suffix is said once. */
export function shortClock(minutes: number): string {
  const hour = Math.floor(minutes / 60) % 24;
  const minute = minutes % 60;
  return `${hour % 12 || 12}${minute ? `:${String(minute).padStart(2, "0")}` : ""}`;
}

/** "5:30 – 7 PM", or "11 AM – 1 PM" when the halves differ. */
export function spanLabel(span: MinuteSpan): string {
  const half = (minutes: number) => (Math.floor(minutes / 60) % 24 >= 12 ? "PM" : "AM");
  return half(span.startMin) === half(span.endMin)
    ? `${shortClock(span.startMin)} – ${clockLabel(span.endMin)}`
    : `${clockLabel(span.startMin)} – ${clockLabel(span.endMin)}`;
}

/** "Hot Stone 90" — the short form a timeline block has room for. */
export function serviceLabel(session: Pick<SessionBlock, "serviceName" | "durationMinutes">) {
  return `${session.serviceName} ${session.durationMinutes}`;
}

/**
 * What the calendar can change. The demo's version edits an in-memory list
 * (calendar.mock-edits.ts); the real one will call the existing endpoints.
 * Each returns an undo when it can offer one, or `null`.
 */
export interface CalendarActions {
  assign: (bookingId: string, therapistId: string) => (() => void) | null;
  /** `shift: null` sets a day off. */
  saveShift: (staffId: string, day: string, shift: MinuteSpan | null) => (() => void) | null;
}

/**
 * The work a shift change would leave uncovered: blocks outside the new hours,
 * or every block on a day off. A FACT for the editor to show — changing a
 * shift never moves a booking, and whether it should is not a recorded rule.
 */
export function blocksOutsideShift(row: BoardRow, shift: MinuteSpan | null): BoardBlock[] {
  return row.blocks.filter(
    (block) => !shift || block.startMin < shift.startMin || block.endMin > shift.endMin,
  );
}

/** "4 – 12 AM" — the design's compact shift, for cells with no room for two suffixes. */
export function compactSpanLabel(span: MinuteSpan): string {
  return `${shortClock(span.startMin)} – ${clockLabel(span.endMin)}`;
}
